import { useEffect, useRef, useState } from "react";
import { createIntent, uploadIntentFile } from "./api";
import type { LocalAttachment } from "./types";

// Build-time only. We deliberately do NOT honor a `?app=` runtime override —
// such an override would let any link of the form
// `https://start.docsumo.com/?app=evil.com` redirect a victim's freshly-minted
// intent token to an attacker domain, where the attacker could then call the
// unauthenticated /preview endpoint and pull the victim's uploaded documents
// via the presigned download URLs it returns. Per-environment targeting is
// done via `VITE_APP_ORIGIN` in `.env.local` at dev time and via the build
// pipeline's env config for staging/production.
const APP_ORIGIN: string =
  import.meta.env.VITE_APP_ORIGIN || "https://app.docsumo.com";

const MAX_FILES = 5;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
const MAX_PROMPT_CHARS = 4096;

// Rotating placeholder examples shown when the textarea is empty. The list
// stays here (not in the marketing site) so we own the copy without a
// Webflow round-trip — same approach as the existing widget.
const PLACEHOLDERS = [
  "e.g. Split this loan packet, extract borrower details, and flag mismatches across documents",
  "e.g. Auto-classify a legal binder, split into individual orders, validate across the set",
  "e.g. Extract loan covenants from commercial agreements and flag non-standard clauses",
  "e.g. 3-way match invoices against POs and receipts, flag any quantity or price mismatches",
  "e.g. Run cross-document KYB checks — registration, UBO filings, sanctions — show what failed and why",
];

function prettySize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Map a filename to a small type badge: a 3-letter label plus a soft tinted
 * color so the eye can pick a doc out of a mixed batch at a glance. Tailwind
 * pastels keep it on-theme — no heavy color blocks fighting the indigo accent.
 */
function fileTypeBadge(filename: string): { label: string; classes: string } {
  const ext = filename.toLowerCase().split(".").pop() ?? "";
  switch (ext) {
    case "pdf":
      return { label: "PDF", classes: "bg-red-50 text-red-600" };
    case "doc":
    case "docx":
      return { label: "DOC", classes: "bg-blue-50 text-blue-600" };
    case "xls":
    case "xlsx":
    case "xlsm":
    case "csv":
      return { label: "XLS", classes: "bg-emerald-50 text-emerald-600" };
    case "ppt":
    case "pptx":
      return { label: "PPT", classes: "bg-orange-50 text-orange-600" };
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "tif":
    case "tiff":
    case "heic":
      return { label: "IMG", classes: "bg-violet-50 text-violet-600" };
    case "txt":
    case "md":
    case "rtf":
      return { label: "TXT", classes: "bg-slate-100 text-slate-600" };
    case "json":
    case "xml":
    case "yaml":
    case "yml":
      return { label: "DAT", classes: "bg-amber-50 text-amber-700" };
    case "zip":
    case "tar":
    case "gz":
    case "rar":
    case "7z":
      return { label: "ZIP", classes: "bg-stone-100 text-stone-600" };
    default:
      return { label: "FILE", classes: "bg-gray-100 text-gray-600" };
  }
}

export default function App() {
  const [prompt, setPrompt] = useState("");
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  // Rotate the placeholder every 3.5s while empty. Pause when the user is
  // typing so we don't disrupt their reading.
  useEffect(() => {
    if (prompt.length > 0) return;
    const id = setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % PLACEHOLDERS.length);
    }, 3500);
    return () => clearInterval(id);
  }, [prompt.length]);

  const totalBytes = attachments.reduce((n, a) => n + a.file.size, 0);

  function addFiles(files: FileList | File[]) {
    setError(null);
    const next: LocalAttachment[] = [];
    let runningTotal = totalBytes;
    for (const file of Array.from(files)) {
      if (attachments.length + next.length >= MAX_FILES) {
        setError(`You can attach up to ${MAX_FILES} documents.`);
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        setError(
          `"${file.name}" is larger than ${prettySize(MAX_FILE_BYTES)} and was skipped.`,
        );
        continue;
      }
      if (runningTotal + file.size > MAX_TOTAL_BYTES) {
        setError(
          `Combined size exceeds ${prettySize(MAX_TOTAL_BYTES)}. Remove a document to add more.`,
        );
        break;
      }
      runningTotal += file.size;
      next.push({ file, uploading: true });
    }
    if (next.length === 0) return;
    setAttachments((prev) => [...prev, ...next]);
    next.forEach((a) => void uploadOne(a.file));
  }

  async function uploadOne(file: File) {
    try {
      const ref = await uploadIntentFile(file);
      setAttachments((prev) =>
        prev.map((a) =>
          a.file === file
            ? { ...a, uploading: false, s3_key: ref.s3_key }
            : a,
        ),
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : "Upload failed";
      setAttachments((prev) =>
        prev.map((a) =>
          a.file === file ? { ...a, uploading: false, error: message } : a,
        ),
      );
    }
  }

  function removeAttachment(file: File) {
    setAttachments((prev) => prev.filter((a) => a.file !== file));
  }

  const promptTooLong = prompt.length > MAX_PROMPT_CHARS;
  const anyUploading = attachments.some((a) => a.uploading);
  const anyUploadFailed = attachments.some((a) => a.error);
  const canSubmit =
    !submitting &&
    prompt.trim().length > 0 &&
    !promptTooLong &&
    !anyUploading &&
    !anyUploadFailed;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const intent = await createIntent({
        prompt: prompt.trim(),
        files: attachments
          .filter((a) => a.s3_key)
          .map((a) => ({
            filename: a.file.name,
            s3_key: a.s3_key!,
            size: a.file.size,
            content_type: a.file.type || "application/octet-stream",
          })),
        template_id: null,
      });
      const dest = `${APP_ORIGIN}/onboard/${intent.intent_token}`;
      // Break out of the iframe — the handoff target is a full-page navigation
      // on the parent, not inside the embed.
      if (window.top) {
        window.top.location.href = dest;
      } else {
        window.location.href = dest;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full p-2">
      <div
        className={`rounded-2xl border bg-white transition-colors ${
          isDragging
            ? "border-docsumo-primary"
            : "border-docsumo-border"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
          }
        }}
      >
        {/* Prompt textarea */}
        <textarea
          ref={promptRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={PLACEHOLDERS[placeholderIdx]}
          rows={4}
          maxLength={MAX_PROMPT_CHARS}
          className="block w-full resize-none rounded-t-2xl bg-transparent px-5 py-4 text-[15px] leading-6 text-docsumo-ink placeholder:text-docsumo-subtleInk/70 focus:outline-none"
          onKeyDown={(e) => {
            // ⌘/Ctrl + Enter to submit, matches the chat-input convention
            // most users expect from prompt boxes.
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />

        {/* Attached file chips — single horizontal row that scrolls when full.
            Same pattern as Claude/ChatGPT input attachments: predictable height
            (no vertical chaos), tight chips, type-coded badge for scannability.
            We let chips shrink to a min before scrolling kicks in so 3-4 fit
            without scrolling for typical iframe widths. */}
        {attachments.length > 0 && (
          <div className="relative px-3 pb-2">
            <ul className="flex flex-nowrap gap-2 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:thin]">
              {attachments.map((a) => {
                const badge = fileTypeBadge(a.file.name);
                return (
                  <li
                    key={a.file.name + a.file.size}
                    className={`group inline-flex shrink-0 items-center gap-2 rounded-lg border bg-white px-2 py-1.5 transition ${
                      a.error
                        ? "border-red-200"
                        : "border-docsumo-border hover:border-docsumo-primary/30 hover:bg-docsumo-softBg/50"
                    }`}
                  >
                    {/* Type badge — colored square with the extension as a tag.
                        Spinner replaces the label while the file uploads. */}
                    <span
                      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10px] font-bold tracking-wider ${badge.classes}`}
                      aria-hidden="true"
                    >
                      {a.uploading ? (
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          className="animate-spin"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeDasharray="42 100"
                            strokeLinecap="round"
                          />
                        </svg>
                      ) : (
                        badge.label
                      )}
                    </span>

                    {/* Two-line text column. Filename truncates to a tight max
                        so chips stay scan-friendly even with long names. */}
                    <div className="flex min-w-0 flex-col leading-tight">
                      <span className="truncate max-w-[10rem] text-[12.5px] font-medium text-docsumo-ink">
                        {a.file.name}
                      </span>
                      <span className="text-[10.5px] text-docsumo-subtleInk">
                        {a.error ? (
                          <span className="text-red-600">{a.error}</span>
                        ) : a.uploading ? (
                          <span className="text-docsumo-primary">Uploading…</span>
                        ) : (
                          prettySize(a.file.size)
                        )}
                      </span>
                    </div>

                    {/* Remove — fades in on hover, always visible on touch / keyboard focus */}
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.file)}
                      aria-label={`Remove ${a.file.name}`}
                      className="ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-docsumo-subtleInk opacity-60 transition hover:bg-docsumo-softBg hover:text-docsumo-ink hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-docsumo-primary/50 group-hover:opacity-100"
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Right-edge fade hint that the row scrolls when it overflows.
                Pointer-events disabled so it never blocks the last chip's
                remove button. */}
            <div
              className="pointer-events-none absolute right-3 top-0 bottom-3 w-8 bg-gradient-to-l from-white to-transparent"
              aria-hidden="true"
            />
          </div>
        )}

        {/* Bottom row: attach pill on the left, submit arrow on the right */}
        <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={attachments.length >= MAX_FILES}
              className="inline-flex items-center gap-1.5 rounded-lg border border-docsumo-border bg-white px-3 py-1.5 text-[13px] font-medium text-docsumo-ink transition hover:border-docsumo-primary/40 hover:bg-docsumo-softBg disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-docsumo-primary/40"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Attach sample docs
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-label={submitting ? "Generating workflow" : "Generate workflow"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-docsumo-primary/10 text-docsumo-primary transition enabled:hover:bg-docsumo-primary enabled:hover:text-white disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-docsumo-primary/50"
          >
            {submitting ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                className="animate-spin"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="42 100"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Error / over-limit messaging — outside the box so the chrome stays clean */}
      {error && (
        <p className="mt-2 px-1 text-[13px] text-red-600" role="alert">
          {error}
        </p>
      )}
      {promptTooLong && (
        <p className="mt-2 px-1 text-[13px] text-red-600">
          Prompt is too long ({prompt.length}/{MAX_PROMPT_CHARS}).
        </p>
      )}
    </div>
  );
}
